import React, { useState } from 'react';
import {
  Button,
  Box,
  Typography,
  Stack,
  FormControl,
  TextField,
  IconButton,
  DialogContent,
  DialogActions,
  Alert,
  MenuItem,
} from '@mui/material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { Close } from '@mui/icons-material';
import { APP_CONFIG } from '../../../../config/app.config';
import { container } from '@infrastructure/container';
import { Connection } from '@domain/connection/models/Connection';
import { ChannelDockAccountType } from '@domain/connection/IMarketplaceRepository';

interface ChannelDockCredentialsFormProps {
  onNewConnection: (connection: Connection) => void;
  onClose: () => void;
  /** When set, re-authorizes this existing connection instead of creating a new one. */
  reconnectConnectionId?: string;
}

const ChannelDockCredentialsForm: React.FC<ChannelDockCredentialsFormProps> = ({
  onNewConnection,
  onClose,
  reconnectConnectionId,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [accountType, setAccountType] = useState<ChannelDockAccountType>('seller');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const apiKeysUrl = accountType === 'center'
    ? 'https://channeldock.com/portal/public/centersettings/api_keys'
    : 'https://channeldock.com/portal/public/settings/api_keys';

  const connect = async () => {
    setError(null);
    setValidationError(null);

    if (!apiKey || !apiSecret) {
      setValidationError('Please fill in the API key and API secret');
      return;
    }

    setIsLoading(true);
    const request = reconnectConnectionId
      ? container.channeldockConnectorRepository.reconnect(reconnectConnectionId, apiKey, apiSecret, accountType)
      : container.channeldockConnectorRepository.addConnection(apiKey, apiSecret, accountType);

    request.then((response) => {
      if (response.status === 'success') {
        onNewConnection(response.data);
      } else {
        setError(response.error?.message || 'Something went wrong');
      }
    }).catch((err) => {
      setError(err.message);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="medium">
            {reconnectConnectionId ? 'Reconnect ChannelDock' : 'Connect ChannelDock'}
          </Typography>
          <IconButton onClick={onClose} color="inherit">
            <Close />
          </IconButton>
        </Stack>
      </DialogHeader>

      <DialogContent>
        <Box>
          <Typography variant="subtitle2" color="text.primary">
            To connect to your ChannelDock account, follow these steps:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <ol style={{ paddingLeft: 20 }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Log in to ChannelDock and open <a href={apiKeysUrl} target="_blank" rel="noopener noreferrer">Settings → API & Webhooks</a>
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  In API keys, click Generate new API keys and give them a description
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Paste the API key and API secret below. Choose Seller or Fulfillment center so the right inventory API is used
                </Typography>
              </li>
            </ol>
          </Typography>
        </Box>

        <FormControl fullWidth>
          <TextField
            select
            label="Account type"
            variant="outlined"
            fullWidth
            margin="normal"
            value={accountType}
            onChange={(e) => setAccountType(e.target.value as ChannelDockAccountType)}
          >
            <MenuItem value="seller">Seller</MenuItem>
            <MenuItem value="center">Fulfillment center</MenuItem>
          </TextField>
          <TextField
            label="API key"
            variant="outlined"
            fullWidth
            margin="normal"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            error={!!validationError}
            helperText={validationError}
          />
          <TextField
            label="API secret"
            type="password"
            variant="outlined"
            fullWidth
            margin="normal"
            value={apiSecret}
            onChange={(e) => setApiSecret(e.target.value)}
            error={!!validationError}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 3, justifyContent: 'flex-end' }}>
        <Button variant="contained" disabled={isLoading} onClick={connect}>
          {reconnectConnectionId ? 'Reconnect' : 'Connect'}
        </Button>
      </DialogActions>
    </>
  );
};

export default ChannelDockCredentialsForm;
